/**
 * Nav glyphs + the icon-bearing nav tree.
 *
 * The tree itself (labels, sections, grouping, role gating, hrefs) is DECLARED in
 * lib/navModel.ts -- a plain .ts file so its rules are unit-tested (navModel.test.ts). This
 * file only attaches the hand-drawn glyphs: a domain icon per top-level domain and a 16px leaf
 * icon per section. The desktop DomainRail renders the fly-outs; the tablet thumb-rail shows
 * NAV_PRIMARY only.
 *
 * Domains (2026-10-07, Customer menu redesign Phase 1): Customer (replaced "Network-wide" +
 * "Network"), Wireless, Hospitality, Switch, Gateway.
 *
 * Icons are hand-drawn inline SVGs -- deliberately generic operational marks, copying NO OEM
 * iconography. Leaves with status "soon" route to the ComingSoon placeholder and show a Soon
 * badge.
 */
import type { ReactNode } from "react";

import {
  NAV_MODEL,
  NAV_PRIMARY_SECTIONS,
  type NavDomainModel,
  type NavGroupModel,
  type NavLeafModel,
} from "@/lib/navModel";

export { KNOWN_SECTIONS } from "@/lib/navModel";

export type NavLeaf = NavLeafModel;
export type NavGroup = NavGroupModel;
export interface NavDomain extends NavDomainModel {
  icon: ReactNode;
}

const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

function Svg({ children }: { children: ReactNode }) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
      {children}
    </svg>
  );
}

// --- Domain glyphs -------------------------------------------------------------------
const IconNetworkWide = (
  <Svg>
    <circle cx="12" cy="12" r="8.5" {...stroke} />
    <path d="M3.5 12h17M12 3.5c2.6 2.4 2.6 14.6 0 17M12 3.5c-2.6 2.4-2.6 14.6 0 17" {...stroke} />
  </Svg>
);
const IconWireless = (
  <Svg>
    <path d="M4 9c4.5-4 11.5-4 16 0M7 12.5c2.8-2.4 7.2-2.4 10 0" {...stroke} />
    <circle cx="12" cy="17" r="1.3" {...stroke} />
  </Svg>
);
const IconHospitality = (
  <Svg>
    {/* a simple "welcome desk / portal" mark */}
    <path d="M4 20V9l8-5 8 5v11" {...stroke} />
    <path d="M9 20v-5a3 3 0 016 0v5" {...stroke} />
  </Svg>
);
const IconSwitch = (
  <Svg>
    <rect x="3" y="8" width="18" height="8" rx="1.5" {...stroke} />
    <path d="M7 16v2M11 16v2M15 16v2M18 16v2M7 11h.01M10 11h.01" {...stroke} />
  </Svg>
);
const IconNetwork = (
  <Svg>
    <path d="M4 7h10M18 7h2M4 17h2M10 17h10" {...stroke} />
    <circle cx="16" cy="7" r="2" {...stroke} />
    <circle cx="8" cy="17" r="2" {...stroke} />
  </Svg>
);
const IconFirewall = (
  <Svg>
    {/* a security shield — AP + switch + firewall in one dashboard (Meraki's single pane) */}
    <path d="M12 3l7 3v5c0 5-3 8-7 10-4-2-7-5-7-10V6z" {...stroke} />
    <path d="M9.5 12l1.8 1.8L15 10" {...stroke} />
  </Svg>
);

/** Domain id -> its rail glyph. */
const DOMAIN_ICONS: Record<string, ReactNode> = {
  // The customer as a whole: a globe (every site of theirs, wherever it is).
  customer: IconNetworkWide,
  wireless: IconWireless,
  hospitality: IconHospitality,
  switch: IconSwitch,
  gateway: IconFirewall,
};

/** The declarative model (lib/navModel) with each domain's glyph attached. */
export const NAV_TREE: NavDomain[] = NAV_MODEL.map((d) => ({ ...d, icon: DOMAIN_ICONS[d.id] }));

/** Section -> thumb-rail glyph (24px). */
const PRIMARY_ICONS: Record<string, ReactNode> = {
  overview: (
    <Svg>
      {/* a table of sites: header rule + rows */}
      <rect x="3.5" y="5" width="17" height="14" rx="2" {...stroke} />
      <path d="M3.5 9.5h17M8 13h9M8 16h6M5.8 13h.01M5.8 16h.01" {...stroke} />
    </Svg>
  ),
  dashboard: <Svg><path d="M3 13h4l2-5 3 9 2-5h7" {...stroke} /></Svg>,
  devices: (
    <Svg>
      <rect x="4" y="5" width="16" height="10" rx="2" {...stroke} />
      <path d="M8 19h8M12 15v4" {...stroke} />
    </Svg>
  ),
  clients: (
    <Svg>
      <circle cx="12" cy="8" r="3.2" {...stroke} />
      <path d="M5 19c0-3.3 3.1-5.5 7-5.5s7 2.2 7 5.5" {...stroke} />
    </Svg>
  ),
  configure: IconNetwork,
};

/** The handful of sections the tablet thumb-rail shows (space-limited). */
export const NAV_PRIMARY: {
  section: string;
  label: string;
  customerLevel?: boolean;
  icon: ReactNode;
}[] = NAV_PRIMARY_SECTIONS.map((p) => ({ ...p, icon: PRIMARY_ICONS[p.section] }));

// --- Per-LEAF glyphs (rendered beside each fly-out item, UniFi-style icon+label) --------
// A compact 16px mark per section. Deliberately generic operational icons; copy NO OEM
// iconography. Looked up by section so NAV_TREE stays declarative.
function Leaf({ children }: { children: ReactNode }) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden className="shrink-0">
      {children}
    </svg>
  );
}

/** Stacked layers: one VLAN list laid across every device of the site (Site VLANs). */
const SITE_VLANS_GLYPH = (
  <Leaf><path d="M12 4l8 4-8 4-8-4z" {...stroke} /><path d="M4 12l8 4 8-4M4 16l8 4 8-4" {...stroke} /></Leaf>
);

/**
 * Section -> 16px fly-out glyph. EVERY nav section must have one (OP#1858 found 10 without);
 * navModel.test.ts reads this file and fails on a section with no entry here.
 */
export const LEAF_ICONS: Record<string, ReactNode> = {
  // "site-vlans" left the nav 2026-09-14 (now Gateway > Site VLANs); the route redirects there.
  "site-vlans": SITE_VLANS_GLYPH,
  // Alerts: a warning triangle.
  alerts: <Leaf><path d="M12 4l9 16H3z" {...stroke} /><path d="M12 10v4M12 17h.01" {...stroke} /></Leaf>,
  // Customer > Monitor
  // Overview: a table of sites -- header rule over rows.
  overview: <Leaf><rect x="3.5" y="5" width="17" height="14" rx="2" {...stroke} /><path d="M3.5 9.5h17M8 13h9M8 16h6" {...stroke} /></Leaf>,
  dashboard: <Leaf><path d="M3 13h4l2-5 3 9 2-5h7" {...stroke} /></Leaf>,
  sites: <Leaf><path d="M4 20V8l6-4 6 4M14 20V4l6 4v12M3 20h18M8 11h.01M8 15h.01" {...stroke} /></Leaf>,
  devices: <Leaf><rect x="4" y="5" width="16" height="10" rx="2" {...stroke} /><path d="M8 19h8M12 15v4" {...stroke} /></Leaf>,
  clients: <Leaf><circle cx="9" cy="9" r="2.6" {...stroke} /><path d="M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5" {...stroke} /><path d="M16 8.2a2.5 2.5 0 010 4.6M17 19c0-2-.7-3.4-2-4.3" {...stroke} /></Leaf>,
  topology: <Leaf><circle cx="12" cy="5" r="2" {...stroke} /><circle cx="5" cy="18" r="2" {...stroke} /><circle cx="19" cy="18" r="2" {...stroke} /><path d="M12 7v4M12 11l-5.5 5M12 11l5.5 5" {...stroke} /></Leaf>,
  audit: <Leaf><path d="M6 3h9l4 4v14H6z" {...stroke} /><path d="M9 12l2 2 4-4" {...stroke} /></Leaf>,
  onboard: <Leaf><rect x="4" y="6" width="12" height="9" rx="2" {...stroke} /><path d="M8 19h6M11 15v4M19 6v6M16 9h6" {...stroke} /></Leaf>,
  // Wireless
  "access-points": <Leaf><path d="M5 10c3.5-3 10.5-3 14 0M8 13c2-1.7 6-1.7 8 0" {...stroke} /><circle cx="12" cy="17" r="1.2" {...stroke} /></Leaf>,
  "rf-spectrum": <Leaf><path d="M3 16l3-6 3 9 3-13 3 11 3-5h3" {...stroke} /></Leaf>,
  ssids: <Leaf><path d="M4 8c4.5-4 11.5-4 16 0" {...stroke} /><path d="M7 11.5c2.8-2.4 7.2-2.4 10 0" {...stroke} /><path d="M10 15c1.2-1 2.8-1 4 0" {...stroke} /><circle cx="12" cy="18.5" r="1" {...stroke} /></Leaf>,
  "access-control": <Leaf><rect x="5" y="10" width="14" height="10" rx="2" {...stroke} /><path d="M8 10V7a4 4 0 018 0v3" {...stroke} /></Leaf>,
  firewall: <Leaf><path d="M4 5h16v6c0 5-4 7-8 8-4-1-8-3-8-8z" {...stroke} /><path d="M12 4v16" {...stroke} /></Leaf>,
  firewalls: <Leaf><path d="M12 3l7 3v5c0 5-3 8-7 10-4-2-7-5-7-10V6z" {...stroke} /></Leaf>,
  // Gateway: a box with WAN in / LAN out arrows.
  gateways: <Leaf><rect x="3" y="9" width="18" height="7" rx="1.5" {...stroke} /><path d="M6.5 12.5h.01M9.5 12.5h.01M12 4v5M10 6l2-2 2 2M12 16v4M10 18l2 2 2-2" {...stroke} /></Leaf>,
  // Route Table: one path entering, diverging by policy -- the per-VLAN tables.
  "route-table": <Leaf><path d="M12 21V11M12 11L5 5M12 11l7-6" {...stroke} /><circle cx="12" cy="21" r="1.4" {...stroke} /><circle cx="5" cy="4" r="1.4" {...stroke} /><circle cx="19" cy="4" r="1.4" {...stroke} /></Leaf>,
  // Security Posture: a shield with an inspection lens.
  "security-center": <Leaf><path d="M12 3l7 3v5c0 5-3 8-7 10-4-2-7-5-7-10V6z" {...stroke} /><circle cx="11.5" cy="11" r="2.5" {...stroke} /><path d="M13.3 12.8l2 2" {...stroke} /></Leaf>,
  "gateway-vlans": SITE_VLANS_GLYPH,
  // DHCP: one server handing addresses down to three clients.
  "gateway-dhcp": <Leaf><rect x="9" y="3" width="6" height="5" rx="1" {...stroke} /><path d="M12 8v4M5 12h14M5 12v3M12 12v3M19 12v3" {...stroke} /><circle cx="5" cy="17.5" r="1.5" {...stroke} /><circle cx="12" cy="17.5" r="1.5" {...stroke} /><circle cx="19" cy="17.5" r="1.5" {...stroke} /></Leaf>,
  // Routing: a signpost (one pole, two directions).
  "gateway-routing": <Leaf><path d="M12 3v18M12 6h6l2 2-2 2h-6M12 13H6l-2 2 2 2h6" {...stroke} /></Leaf>,
  // Traffic Shaping: a speed gauge.
  "traffic-shaping": <Leaf><path d="M4 17a8 8 0 1116 0" {...stroke} /><path d="M12 17l4-5" {...stroke} /><circle cx="12" cy="17" r="1.2" {...stroke} /></Leaf>,
  // Threat Protection: a bug (malware / intrusion), not another shield.
  "threat-protection": <Leaf><rect x="8" y="7" width="8" height="12" rx="4" {...stroke} /><path d="M12 7V5M9.5 5.5L8 4M14.5 5.5L16 4M8 11H4M8 15H4.5M16 11h4M16 15h3.5" {...stroke} /></Leaf>,
  // Content Filtering: a funnel.
  "content-filtering": <Leaf><path d="M4 5h16l-6 7v6l-4 2v-8z" {...stroke} /></Leaf>,
  // Gateway Access Control: a gate (edge policy), distinct from the Wi-Fi padlock.
  "gateway-access-control": <Leaf><path d="M4 20V5M20 20V5M4 9h16M4 15h16M9 9v6M14 9v6" {...stroke} /></Leaf>,
  "gateway-firewall": <Leaf><path d="M12 3l7 3v5c0 5-3 8-7 10-4-2-7-5-7-10V6z" {...stroke} /><path d="M9 9h6M9 12h6M9 15h4" {...stroke} /></Leaf>,
  radio: <Leaf><circle cx="12" cy="12" r="8" {...stroke} /><path d="M12 12l4-3" {...stroke} /><circle cx="12" cy="12" r="1.3" {...stroke} /></Leaf>,
  // AP Port Profiles: a wall plate with two jacks (the in-room AP's ports).
  "port-profiles": <Leaf><rect x="5" y="3" width="14" height="18" rx="2" {...stroke} /><rect x="8.5" y="6.5" width="7" height="4" rx="0.8" {...stroke} /><rect x="8.5" y="13.5" width="7" height="4" rx="0.8" {...stroke} /></Leaf>,
  // Mesh: three APs, each linked to both others (Topology's glyph is a tree, this a full mesh).
  mesh: <Leaf><circle cx="12" cy="5" r="2" {...stroke} /><circle cx="5" cy="18" r="2" {...stroke} /><circle cx="19" cy="18" r="2" {...stroke} /><path d="M10.9 6.8L6.1 16.2M13.1 6.8l4.8 9.4M7 18h10" {...stroke} /></Leaf>,
  "multi-psk": <Leaf><circle cx="8" cy="9" r="3" {...stroke} /><path d="M10.5 11.5L18 19M15 16l2-2M17 14l2 2" {...stroke} /></Leaf>,
  // Hospitality
  "guest-portal": <Leaf><path d="M5 20V6a2 2 0 012-2h7v16M14 12h.5M14 4l5 3v13M5 20h16" {...stroke} /></Leaf>,
  passpoint: <Leaf><circle cx="12" cy="12" r="8" {...stroke} /><path d="M4 12h16M12 4c2.5 2.3 2.5 13.7 0 16M9 20l2 2 3.5-4" {...stroke} /></Leaf>,
  vouchers: <Leaf><path d="M3 8a2 2 0 012-2h14a2 2 0 012 2 2 2 0 000 4 2 2 0 000 4 2 2 0 01-2 2H5a2 2 0 01-2-2 2 2 0 000-4 2 2 0 000-4z" {...stroke} /><path d="M14 8v8" strokeDasharray="1 2" {...stroke} /></Leaf>,
  pms: <Leaf><path d="M3 17v-5a2 2 0 012-2h14a2 2 0 012 2v5M3 17h18M3 17v2M21 17v2M6 10V8a1 1 0 011-1h3a1 1 0 011 1v2" {...stroke} /></Leaf>,
  // Usage: a throughput meter -- bars of differing height.
  usage: <Leaf><path d="M4 19V11M9 19V5M14 19V14M19 19V8" {...stroke} /><path d="M3 21h18" {...stroke} /></Leaf>,
  // Firmware Compliance: the firmware chip mark with a check.
  "firmware-compliance": <Leaf><rect x="4" y="4" width="12" height="12" rx="2" {...stroke} /><path d="M7 8h6M7 11h4" {...stroke} /><path d="M13 17l2.5 2.5L21 14" {...stroke} /></Leaf>,
  "walled-gardens": <Leaf><path d="M6 20V9l3-3 3 3-3 3M12 20V6l3-3 3 3v14M4 20h16M9 12v8M15 9v11" {...stroke} /></Leaf>,
  // Switch
  switches: <Leaf><rect x="3" y="8" width="18" height="8" rx="1.5" {...stroke} /><path d="M7 16v2M11 16v2M15 16v2M18 16v2M6.5 12h.01M9.5 12h.01" {...stroke} /></Leaf>,
  "switch-ports": <Leaf><rect x="3" y="6" width="18" height="12" rx="1.5" {...stroke} /><path d="M6 10v4M9 10v4M12 10v4M15 10v4M18 10v4" {...stroke} /></Leaf>,
  multicast: <Leaf><circle cx="5" cy="12" r="2" {...stroke} /><circle cx="18" cy="6" r="2" {...stroke} /><circle cx="18" cy="12" r="2" {...stroke} /><circle cx="18" cy="18" r="2" {...stroke} /><path d="M7 12h4M11 12l5-5M11 12h5M11 12l5 5" {...stroke} /></Leaf>,
  "switch-vlans": <Leaf><path d="M4 7l7-3 9 4v6l-9 4-7-3z" {...stroke} /><path d="M11 4v14" {...stroke} /><circle cx="16" cy="9" r="1" {...stroke} /></Leaf>,
  "switch-poe": <Leaf><rect x="4" y="8" width="16" height="8" rx="1.5" {...stroke} /><path d="M12 5l-2 4h4l-2 4" {...stroke} /></Leaf>,
  "switch-portprofiles": <Leaf><path d="M4 7h9M17 7h3M4 12h3M11 12h9M4 17h13M20 17h0" {...stroke} /><circle cx="15" cy="7" r="1.6" {...stroke} /><circle cx="9" cy="12" r="1.6" {...stroke} /><circle cx="18" cy="17" r="1.6" {...stroke} /></Leaf>,
  // Customer > Configure
  configure: <Leaf><circle cx="12" cy="12" r="3" {...stroke} /><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" {...stroke} /></Leaf>,
  templates: <Leaf><path d="M12 3l8 4-8 4-8-4z" {...stroke} /><path d="M4 12l8 4 8-4M4 16l8 4 8-4" {...stroke} /></Leaf>,
  "device-import": <Leaf><path d="M12 3v10" {...stroke} /><path d="m8.5 9.5 3.5 3.5 3.5-3.5" {...stroke} /><path d="M4 16v3.5h16V16" {...stroke} /></Leaf>,
  operators: <Leaf><circle cx="9" cy="8" r="3" {...stroke} /><path d="M3.5 19a5.5 5.5 0 0 1 11 0" {...stroke} /><path d="M16 5.5a3 3 0 0 1 0 5.8M17 19a5.6 5.6 0 0 0-1.6-3.9" {...stroke} /></Leaf>,
  "radius-servers": <Leaf><rect x="4" y="4" width="16" height="6" rx="1.5" {...stroke} /><rect x="4" y="14" width="16" height="6" rx="1.5" {...stroke} /><path d="M7 7h.01M7 17h.01" {...stroke} /></Leaf>,
  // Wi-Fi users: a person beside a Wi-Fi arc.
  "network-users": <Leaf><circle cx="8" cy="9" r="2.6" {...stroke} /><path d="M3 19c0-2.8 2.2-4.8 5-4.8s5 2 5 4.8" {...stroke} /><path d="M15 9.5c2-1.8 4.5-1.8 6.5 0M16.8 12c.9-.8 2-.8 2.9 0" {...stroke} /></Leaf>,
  // RADIUS realms: a domain "@" mark.
  "radius-realms": <Leaf><circle cx="12" cy="12" r="3" {...stroke} /><path d="M15 12v1.5a2.5 2.5 0 005 0V12a8 8 0 10-3.2 6.4" {...stroke} /></Leaf>,
  // Notifications: a bell.
  notifications: <Leaf><path d="M6 16V11a6 6 0 0112 0v5l1.5 2h-15z" {...stroke} /><path d="M10 20.5a2 2 0 004 0" {...stroke} /></Leaf>,
  // MSP portal (all customers): stacked tenant cards.
  customers: <Leaf><rect x="7" y="4" width="13" height="10" rx="1.5" {...stroke} /><path d="M4 8v10.5A1.5 1.5 0 005.5 20H16" {...stroke} /></Leaf>,
  firmware: <Leaf><rect x="6" y="4" width="12" height="12" rx="2" {...stroke} /><path d="M9 8h6M9 11h6M12 16v4M9 19l3 2 3-2" {...stroke} /></Leaf>,
};
